import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, X, CheckCircle2, Bot, User, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ts, P, F } from '../api';
import { NumberSearch, CartPanel } from '../NumberPicker';
import { Page, Section, Field, Select, Pill, VoicePill, SmsPill, Loading, ErrorNote, Empty, useLoad, useTS, money } from '../ui';

// Numbers (owner, Oct 31 2026): the main number first, then every other number;
// "+ Add number" runs the same pick-an-area-code → pay → provision flow as signup,
// and each number gets a label, a person and/or an AI avatar.

const STEP_PROGRESS = { selected: 25, purchasing: 50, purchased: 75, voice_ready: 100 };

function AvatarBadge({ number }) {
  const ai = number.role === 'main' || number.answer_mode === 'ai';
  const name = ai ? (number.avatar?.name || 'AI') : (number.assigned_member_name || '');
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '—';
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${ai ? 'bg-violet-500/15 text-violet-600 dark:text-violet-400' : 'bg-muted text-muted-foreground'}`}
      title={ai ? `AI avatar: ${name}` : name || 'Nobody assigned'}>
      {ai ? (number.avatar?.name ? initials : <Bot className="w-4 h-4" />) : initials}
    </div>
  );
}

export default function TSNumbers() {
  const session = useTS();
  const numbers = useLoad(() => ts.numbers());
  const staff = useLoad(() => ts.staff());
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const canBuy = session.can(P.BILLING) && session.company?.status === 'active';

  if (numbers.loading && !numbers.data) return <Loading />;
  const nums = numbers.data || [];
  const main = nums.find(n => n.role === 'main');
  const rest = nums.filter(n => n.role !== 'main');
  const reload = () => { numbers.reload(); staff.reload(); };

  return (
    <Page title="Numbers" subtitle={`${nums.length} number${nums.length === 1 ? '' : 's'} on this account. Your main number is answered by your AI.`}
      actions={canBuy && <Button size="sm" onClick={() => setAdding(true)} data-testid="ts-add-number"><Plus className="w-4 h-4 mr-1" /> Add number</Button>}>
      <ErrorNote error={numbers.error} />
      <Section title="Main number">
        {main ? <NumberRow number={main} onEdit={() => setEditing(main)} /> : <Empty>No main number yet.</Empty>}
      </Section>
      <Section title="All numbers">
        {rest.length === 0 ? <Empty>No other numbers yet.</Empty> : (
          <ul className="divide-y divide-border" data-testid="ts-numbers-list">
            {rest.map(n => <li key={n.id}><NumberRow number={n} onEdit={() => setEditing(n)} /></li>)}
          </ul>
        )}
      </Section>
      {editing && (
        <NumberEditor number={editing} staff={staff.data || []} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}
      {adding && (
        <AddNumber onClose={() => { setAdding(false); reload(); session.refresh(); }}
          onReady={(n) => { setAdding(false); reload(); session.refresh(); setEditing(n); }} />
      )}
    </Page>
  );
}

function NumberRow({ number: n, onEdit }) {
  const ai = n.role === 'main' || n.answer_mode === 'ai';
  return (
    <div className="flex flex-wrap items-center gap-3 py-2" data-testid={`ts-number-row-${n.id}`}>
      <AvatarBadge number={n} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{n.display}</span>
          {n.role === 'main' && <Pill tone="violet">Main</Pill>}
          {n.toll_free && <Pill tone="blue">Toll-free</Pill>}
          {n.label && <span className="text-sm text-muted-foreground" data-testid="ts-number-label">{n.label}</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5" data-testid="ts-number-answered-by">
          {ai ? <>Answered by AI{n.avatar?.name ? ` · ${n.avatar.name}` : ''}{n.assigned_member_name ? ` · transfers to ${n.assigned_member_name}` : ''}</>
            : n.assigned_member_name ? <>Rings {n.assigned_member_name} · AI answers if missed</> : 'Nobody assigned · AI or voicemail answers'}
        </div>
      </div>
      <div className="flex flex-wrap gap-1"><VoicePill status={n.status} /><SmsPill status={n.sms_status} /></div>
      <Button size="sm" variant="outline" onClick={onEdit} data-testid={`ts-number-edit-${n.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Set up</Button>
    </div>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-card border border-border rounded-xl shadow-xl" data-testid={testid}>
        <div className="flex items-center justify-between px-5 h-12 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberEditor({ number, staff, onClose, onSaved }) {
  const session = useTS();
  const voices = useLoad(() => ts.avatarVoices());
  const isMain = number.role === 'main';
  const aiAllowed = isMain || session.has(F.AI_FRONT_DESK);
  const [form, setForm] = useState({
    label: number.label || '',
    answer_mode: isMain ? 'ai' : (number.answer_mode || 'person'),
    member_id: number.assigned_member_id || '',
    avatar: { name: number.avatar?.name || '', voice: number.avatar?.voice || '', greeting: number.avatar?.greeting || '' },
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: typeof v === 'object' && v?.target ? v.target.value : v }));
  const setAv = (k) => (v) => setForm(f => ({ ...f, avatar: { ...f.avatar, [k]: typeof v === 'object' && v?.target ? v.target.value : v } }));
  const active = staff.filter(m => !m.status || m.status === 'active');

  const save = async () => {
    setBusy(true);
    try {
      await ts.updateNumber(number.id, { label: form.label, answer_mode: form.answer_mode, avatar: form.avatar });
      if (!isMain && form.member_id !== (number.assigned_member_id || '')) {
        if (number.assigned_member_id) await ts.unassignNumber(number.assigned_member_id);
        if (form.member_id) await ts.assignNumber(form.member_id, number.id);
      }
      toast.success('Number saved');
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Set up ${number.display}`} onClose={onClose} testid="ts-number-editor">
      <Field label="Label" hint="What this number is for, e.g. Sales line, Spanish line, Google Ads.">
        <Input value={form.label} maxLength={40} onChange={set('label')} placeholder="Sales line" data-testid="ts-number-label-input" />
      </Field>
      {isMain ? (
        <p className="text-sm text-muted-foreground">Your main number is always answered by your AI, trained on your website and AI Brain files. It transfers callers to an available salesperson when they ask for a person.</p>
      ) : (
        <>
          <Field label="Who answers">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button type="button" onClick={() => set('answer_mode')('person')} data-testid="ts-answer-person"
                className={`text-left rounded-lg border px-3 py-2 text-sm ${form.answer_mode === 'person' ? 'border-violet-500 bg-violet-500/5' : 'border-border'}`}>
                <div className="flex items-center gap-2 font-medium"><User className="w-4 h-4" /> A person</div>
                <div className="text-xs text-muted-foreground">Rings the assigned user. The AI answers if they miss it.</div>
              </button>
              <button type="button" disabled={!aiAllowed} onClick={() => set('answer_mode')('ai')} data-testid="ts-answer-ai"
                className={`text-left rounded-lg border px-3 py-2 text-sm disabled:opacity-50 ${form.answer_mode === 'ai' ? 'border-violet-500 bg-violet-500/5' : 'border-border'}`}>
                <div className="flex items-center gap-2 font-medium"><Bot className="w-4 h-4" /> AI avatar</div>
                <div className="text-xs text-muted-foreground">{aiAllowed ? 'Your AI answers first, as the avatar below.' : 'Pro and Business plans.'}</div>
              </button>
            </div>
          </Field>
          <Field label={form.answer_mode === 'ai' ? 'User the AI transfers to' : 'Assigned user'}>
            <Select value={form.member_id} onChange={set('member_id')} data-testid="ts-number-member"
              options={[['', 'Nobody'], ...active.map(m => [m.id, `${m.full_name}${m.assigned_number && m.assigned_number.id !== number.id ? ` (has ${m.assigned_number.display})` : ''}`])]} />
          </Field>
        </>
      )}
      {(isMain || form.answer_mode === 'ai') && (
        <div className="space-y-3 rounded-lg border border-border p-3" data-testid="ts-avatar-form">
          <div className="text-sm font-medium text-foreground">AI avatar</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name"><Input value={form.avatar.name} maxLength={40} onChange={setAv('name')} placeholder="Ava" data-testid="ts-avatar-name" /></Field>
            <Field label="Voice">
              <Select value={form.avatar.voice} onChange={setAv('voice')} data-testid="ts-avatar-voice"
                options={[['', 'Default voice'], ...(voices.data || []).map(v => [v.id, `${v.name} · ${v.gender} · ${v.traits}`])]} />
            </Field>
          </div>
          <Field label="Greeting" hint="{company} and {assistant} are filled in for you. Leave empty to use the AI Front Desk greeting.">
            <Textarea rows={2} maxLength={300} value={form.avatar.greeting} onChange={setAv('greeting')}
              placeholder="Thanks for calling {company}, this is {assistant}. How can I help?" data-testid="ts-avatar-greeting" />
          </Field>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} data-testid="ts-number-save">Save</Button>
      </div>
    </Modal>
  );
}

function AddNumber({ onClose, onReady }) {
  const session = useTS();
  const [cart, setCart] = useState(null);
  const [step, setStep] = useState('pick');
  const [jobKey, setJobKey] = useState(null);
  const [prov, setProv] = useState(null);
  const [repicking, setRepicking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pricing = session.pricing || {};
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (session.company?.adding_seats ? ts.cart() : ts.addSeats(1)).then(setCart).catch(e => setError(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== 'provisioning') return undefined;
    let alive = true;
    const load = () => ts.provisioning().then(d => { if (alive) setProv(d); }).catch(() => {});
    load();
    const t = setInterval(load, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [step]);

  const job = (prov?.jobs || []).find(j => j.key === jobKey);
  const mine = job ? (prov.numbers || []).filter(n => job.number_ids.includes(n.id)) : [];
  const progress = mine.length ? Math.min(...mine.map(n => STEP_PROGRESS[n.status] ?? 10)) : 10;
  const ready = mine.length > 0 && mine.every(n => n.status === 'voice_ready');
  const unavailable = mine.find(n => n.status === 'unavailable');
  const failed = mine.find(n => n.status === 'failed');

  const close = async () => {
    if (step === 'pick') { try { await ts.cancelAddSeats(); } catch (e) { /* nothing was charged */ } }
    onClose();
  };
  const pay = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await ts.checkout();
      setJobKey(r.job_key);
      setStep('provisioning');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const repick = async (e164) => {
    setBusy(true);
    try { await ts.repick(repicking, e164); setRepicking(null); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal title="Add a number" onClose={close} testid="ts-add-number-dialog">
      <ErrorNote error={error} />
      {step === 'pick' && (
        <>
          <p className="text-sm text-muted-foreground">Search by area code and pick from up to 20 numbers. Each number is one seat: {money(pricing.seat_price_cents || 0)} a month, and today you pay only the prorated part of this billing cycle.</p>
          {!cart ? <Loading /> : (
            <>
              <NumberSearch cart={cart} onCart={setCart} />
              <CartPanel cart={cart} onCart={setCart} />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={close}>Cancel</Button>
                <Button disabled={!cart.complete || busy} onClick={pay} data-testid="ts-add-number-pay">Pay and add number</Button>
              </div>
            </>
          )}
        </>
      )}
      {step === 'provisioning' && (
        <div className="space-y-4" data-testid="ts-provisioning-progress">
          {!ready ? (
            <>
              <div className="text-sm font-medium text-foreground">Provisioning your new number…</div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-violet-500 transition-all duration-700" style={{ width: `${progress}%` }} data-testid="ts-provisioning-bar" />
              </div>
              <ul className="text-xs text-muted-foreground space-y-1">
                {mine.map(n => <li key={n.id} className="flex items-center gap-2">{n.display} <VoicePill status={n.status} /></li>)}
              </ul>
              {unavailable && !repicking && (
                <div className="text-sm">That number was taken before we could buy it. Nothing was bought for it. <Button size="sm" variant="outline" onClick={() => setRepicking(unavailable.id)}>Pick another number</Button></div>
              )}
              {repicking && <NumberSearch onPick={repick} />}
              {failed && <p className="text-sm text-red-500">{failed.last_error || 'Setting up this number needs our team.'} We'll finish it and let you know.</p>}
            </>
          ) : (
            <div className="space-y-3" data-testid="ts-provisioning-done">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Your new number is ready</div>
              {mine.map(n => <div key={n.id} className="text-lg font-semibold text-foreground">{n.display}</div>)}
              <p className="text-sm text-muted-foreground">It takes calls now. Give it a label, assign a user, or put your AI avatar on it.</p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={onClose}>Done</Button>
                <Button onClick={() => onReady(mine[0])} data-testid="ts-new-number-setup">Set up this number</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
